import Image from 'next/image';
import { Link } from '@/navigation';
import { Page, CoursePicture } from '@/payload/payload-types';
import RichTextLexical from '../../_components/RichTextLexical';
import { Chip } from '../../_components/Chip';
import ArrowRightLong from '../../_icons/Arrow-right-long';
import { useLocale, useTranslations } from 'next-intl';
import { formatPrice } from '../../_utilities/format-price';
type ProgrammeCardBlockType = Extract<Page['layoutBlocks'][0], { blockType: 'programmeCardBlock' }>;
type ProgrammeCardBlockProps = Omit<ProgrammeCardBlockType, 'blockType'>;

export const CardWithoutKeyvalues: React.FC<{
	cards: ProgrammeCardBlockProps['programmeCards'];
}> = ({ cards }) => {
	const t = useTranslations();
	const locale = useLocale();

	return (
		<div
			className="grid place-content-center gap-4"
			style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))' }}
		>
			{cards &&
				cards.map(
					({ description, title, image, studyLanguage, studyMode, price, badge, id }) => {
						const studyModeText = {
							Cycle: t('cycle-studies'),
							Daytime: t('daytime-studies'),
						};
						return (
							<Link
								key={`${title}-${id}`}
								href={{
									pathname: '/', //TODO: must use fullSlug here one day it is ready
								}}
								className={`group border-gold-light relative h-[500px] w-full max-w-[400px] cursor-pointer rounded-4xl border lg:w-[400px]`}
							>
								<div className="relative h-full w-full">
									<div className="bg-gold-light flex h-full flex-col items-center justify-between space-y-6 rounded-4xl">
										<div className="h-[224px] w-auto rounded-4xl bg-slate-200">
											{(image as CoursePicture).sizes!.card!.url! && (
												<Image
													alt={(image as CoursePicture)?.alt}
													className="rounded-4xl"
													src={(image as CoursePicture).sizes!.card!.url!}
													width={398}
													height={188}
												/>
											)}
										</div>

										<section className="line-clamp-2 flex h-[424px] w-[332px] flex-col justify-between max-[330px]:max-w-[234px] max-[330px]:p-4">
											<div className="flex flex-col space-y-6">
												<div className="space-y-1">
													<p className="text-muted-navy flex text-xs font-bold tracking-[0.48px] uppercase">
														{`${studyModeText[studyMode]} | ${studyLanguage}`}
													</p>

													<RichTextLexical
														className="text-xl leading-8 font-bold text-white"
														content={title}
													/>
												</div>
												<RichTextLexical
													className="text-sm leading-6 font-light text-white"
													content={description}
												/>
											</div>
											<div className="mb-8 flex items-center justify-end">
												<Chip size="small">{`${formatPrice(price, locale, 'EUR')} / ${t('semester')}`}</Chip>
											</div>
										</section>
									</div>

									<div className="absolute inset-0">
										<div className="relative h-full w-full rounded-4xl border-[12px] border-white transition-all delay-100 duration-300 ease-out group-hover:border-[0px]"></div>
									</div>
								</div>
								{badge && (
									<div className="bg-error absolute top-6 right-0 max-w-28 rounded-tl-3xl rounded-bl-3xl py-2 pr-2 pl-4 text-center">
										<span className="text-sm leading-4 font-bold text-white">
											{badge}
										</span>
									</div>
								)}
							</Link>
						);
					},
				)}
		</div>
	);
};

export const CardWithKeyvalues: React.FC<{
	cards: ProgrammeCardBlockProps['programmeCardsWithKeyValues'];
}> = ({ cards }) => {
	const t = useTranslations();
	const locale = useLocale();

	return (
		<div
			className="grid place-content-center gap-4"
			style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))' }}
		>
			{cards &&
				cards.map(
					({
						id,
						description,
						title,
						image,
						studyLanguage,
						studyMode,
						price,
						badge,
						keyValues,
					}) => {
						const studyModeText = {
							Cycle: t('cycle-studies'),
							Daytime: t('daytime-studies'),
						};
						return (
							<Link
								key={`${title}-${id}`}
								href={{
									pathname: '/', //TODO: must use fullSlug here one day it is ready
								}}
								className={`group border-gold-light relative h-[600px] w-full max-w-[400px] cursor-pointer rounded-4xl border lg:w-[400px]`}
							>
								<div className="relative h-full w-full">
									<div className="bg-gold-light flex h-full flex-col items-center justify-between space-y-6 rounded-4xl">
										<div className="h-[224px] w-auto rounded-4xl bg-slate-200">
											{(image as CoursePicture).sizes!.card!.url! && (
												<Image
													alt={(image as CoursePicture)?.alt}
													className="rounded-4xl"
													src={(image as CoursePicture).sizes!.card!.url!}
													width={398}
													height={188}
												/>
											)}
										</div>

										<section className="line-clamp-2 flex h-[424px] w-[332px] flex-col justify-between max-[330px]:max-w-[234px] max-[330px]:p-4">
											<div className="flex flex-col space-y-6">
												<div className="space-y-1">
													<p className="text-muted-navy flex text-xs font-bold tracking-[0.48px] uppercase">
														{`${studyModeText[studyMode]} | ${studyLanguage}`}
													</p>

													<RichTextLexical
														className="text-xl leading-8 font-bold text-white"
														content={title}
													/>
												</div>
												<RichTextLexical
													className="text-sm leading-6 font-light text-white"
													content={description}
												/>

												<div className="flex flex-col space-y-1">
													<p className="text-sm leading-6 font-bold text-white">
														{keyValues.title}
													</p>
													<ul className="p-0">
														{keyValues.values.map(({ value }, i) => (
															<li
																key={i}
																className="flex items-center space-x-2 p-0 text-sm leading-6 font-light text-white"
															>
																<ArrowRightLong fill="#293455" />
																<RichTextLexical content={value} />
															</li>
														))}
													</ul>
												</div>
											</div>
											<div className="mb-8 flex items-center justify-end">
												<Chip size="small">{`${formatPrice(price, locale, 'EUR')} / ${t('semester')}`}</Chip>
											</div>
										</section>
									</div>

									<div className="absolute inset-0">
										<div className="relative h-full w-full rounded-4xl border-[12px] border-white transition-all delay-100 duration-300 ease-out group-hover:border-[0px]"></div>
									</div>
								</div>
								{badge && (
									<div className="bg-error absolute top-6 right-0 max-w-28 rounded-tl-3xl rounded-bl-3xl py-2 pr-2 pl-4 text-center">
										<span className="text-sm leading-4 font-bold text-white">
											{badge}
										</span>
									</div>
								)}
							</Link>
						);
					},
				)}
		</div>
	);
};

export const ProgrammeCardBlock: React.FC<ProgrammeCardBlockProps> = ({
	programmeCards,
	programmeCardsWithKeyValues,
	includeKeyValues,
}) => {
	return (
		<div>
			{includeKeyValues && programmeCardsWithKeyValues ? (
				<CardWithKeyvalues cards={programmeCardsWithKeyValues} />
			) : (
				programmeCards && <CardWithoutKeyvalues cards={programmeCards} />
			)}
		</div>
	);
};
